<template>
    <form
        autocomplete="off"
        data-cy="register-card-form"
        class="p-register-card-form"
        @submit.prevent="submit"
    >
        <toast
            v-if="general_message"
            :type="is_success ? 'success' : 'error'"
            :cdn-url="cdn_url"
            :message="$t(general_message)"
            @hide="() => general_message = null"
        />

        <div class="form-con">
            <h5 class="form-title" v-html="boxSlogan" />
            <progress-bar :stepNo="step_no"></progress-bar>
            <h3 v-if="provider" class="text-center">{{ 'Finish your registration with' | translate }} {{ provider | capitalize }}</h3>
            <div class="form-group" v-if="step_no == 1">
                <label>{{ 'I am a' | translate }}</label>
                <select-wrapper>
                    <select data-cy="preference-select" class="form-control" v-model="preference">
                        <option v-for="(val, key) in preferences" :key="key" :value="val.value">{{ val.label }}</option>
                    </select>
                </select-wrapper>
            </div>
            <div class="form-group" v-if="step_no == 2">
                <label>{{ 'Location' | translate }}</label>
                <div class="province-country-select-wrapper">
                    <select-wrapper v-if="countries.length >= 2">
                        <select data-cy="countries-select" class="form-control" ref="select-country" v-model="country" @change="loadProvinces()">
                            <option v-for="(val, key) in countries" :key="key" :value="val.id">{{ val.name }}</option>
                        </select>
                    </select-wrapper>
                    <select-wrapper>
                        <select data-cy="provinces-select" class="form-control" ref="select-province" v-model="province">
                            <option v-for="(val, key) in provinces" :key="key" :value="val.id">{{ val.name }}</option>
                        </select>
                    </select-wrapper>
                </div>
            </div>
            <div class="form-group" v-if="step_no == 3">
                <label>{{ 'Age' | translate }}</label>
                <select-wrapper>
                    <select data-cy="age-select" class="form-control" v-model="age.val">
                        <option v-for="(val, key) in ages" :key="key" :value="val">{{ `${val} ${$t('years')}` }} </option>
                    </select>
                </select-wrapper>
            </div>
            <div class="form-group" v-if="step_no == 4">
                <label>{{ 'Nickname' | translate }}</label>
                <div :class="['input-wrapper', { 'with-spinner': nick_name.show_loading }]">
                    <input data-cy="nickname-field" :class="`form-control ${nick_name.show_loading ? 'with-spinner' : ''}`" type="text" v-model="nick_name.val" name="nick_name"
                    :minlength="nick_name['min']" :maxlength="nick_name_limit"
                    @input="validateInput(null,'nick_name')"  @keydown.enter.prevent="validateInput(null,'nick_name')" @paste="validateInput($event,'nick_name')"
                    @blur="checkUniqueNickname()" ref="nick-name-input">
                    <i v-if="nick_name.show_loading" class="fa fa-spinner fa-pulse"></i>
                </div>
                <form-notification v-if="nick_name.error_msg" data-cy="nickname-error" :message="nick_name.error_msg | translate" />
            </div>
            <div class="form-group" v-if="open_register_with">
                <div class="btn-con oauth">
                    <button :class="`btn btn-${open_register_with}-oauth`" @click.prevent="redirectOauth(open_register_with)">
                        <i :class="`icon icon-${open_register_with}`"></i>
                        <span class="text-bigger">{{ `Connect with ${capitalize(open_register_with)}` | translate }}</span>
                    </button>
                </div>
                <div class="row btn-wrapper oauth">
                    <div class="col-xs-6">
                        <button class="btn btn-grey" @click.prevent="cancelRegistrationWithOauth">
                            <span>{{ 'Or turn back' | translate }}</span>
                        </button>
                    </div>
                </div>
            </div>
            <div class="form-group" v-if="step_no == 5 && !open_register_with">
                <div v-if="oauthProviders.length && !provider" class="btn-con oauth">
                    <button v-for="(val, key) in oauthProviders" :key="key" :class="`btn btn-${val}-oauth`" @click.prevent="redirectOauth(val)">
                        <i :class="`icon icon-${val}`"></i>
                        <span class="icon-bg"></span>
                        <span>{{ `Connect with ${capitalize(val)}` | translate }}</span>
                    </button>
                    <p class="divider">{{ 'OR' | translate }}</p>
                </div>
                <form-notification
                  v-if="error_goauth"
                  data-cy="error-google-connect"
                  class="google-connect"
                  :message="'Please use the same email you registered.' | translate"
                />

                <label>{{ 'Email' | translate }}</label>
                <vue-simple-suggest class="auto-suggest" :styles="email.show_loading ? {inputWrapper: 'with-spinner'} : {}" :list="emails_suggest" :filter-by-query="true" @hide-list="hidelist"
                :max-suggestions="10" @select="selectedEmail" @hover="hoverSuggest" ref="suggest-email">
                    <input data-cy="email-suggest-field" class="form-control" type="text" :placeholder="emailPlaceholder" :disabled="provider"
                    v-model="email.val" name="email" :autocomplete="email_ac" @blur.prevent="checkEmail(true)" @focus.once="hoverSuggest" @input="checkEmail()" ref="email-input">
                    <i v-if="email.show_loading" class="fa fa-spinner fa-pulse"></i>
                </vue-simple-suggest>
                <form-notification
                  v-if="email.suggestion"
                  class="suggestion"
                  :message="$t('Did you mean {suggestion}?', {suggestion: email.suggestion})"
                  @click="replaceEmailWithSuggestion"
                />
            </div>
            <div v-if="step_no == 5 && !open_register_with" class="form-group">
                <form-notification v-if="email.error_msg" data-cy="email-error" :message="email.error_msg | translate" />
                <template v-if="display_terms">
                    <p class="info-text">
                        <label :class="`for-terms label-checkbox norm square ${check_terms.val ? 'active': ''}`">
                            <input type="checkbox" class="checkbox" v-model="check_terms.val" id="checkterms" name="checkterms" :value="check_terms.val">
                        </label>
                        <label class="checkbox-text for-terms" for="checkterms">
                            {{ 'By checking this box and clicking continue I accept the' | translate }}
                            <template v-if="langIsGerman">
                                <router-link :to="{ path: $t('privacy') }" target='_blank'> {{ 'rg_privacy' | translate }}</router-link>,
                                <router-link :to="{ path: $t('terms-and-conditions') }" target='_blank'> {{ 'rg_terms' | translate }}</router-link>,
                                <router-link :to="{ path: $t('cookie-policy') }" target='_blank'> {{ 'rg_cookies' | translate }}</router-link>,
                            </template>
                            <template v-else>
                                <router-link :to="{ path: $t('terms-and-conditions') }" target='_blank'> {{ 'rg_terms' | translate }}</router-link>,
                                <router-link :to="{ path: $t('privacy') }" target='_blank'> {{ 'rg_privacy' | translate }}</router-link>,
                                <router-link :to="{ path: $t('cookie-policy') }" target='_blank'> {{ 'rg_cookies' | translate }}</router-link>,
                            </template>
                            {{ 'I am aware of the use of fictitious profiles on the website and I confirm that I am of legal age.' | translate }}
                        </label>
                    </p>
                    <form-notification
                      v-if="check_terms.error_msg"
                      data-cy="terms-error"
                      :message="check_terms.error_msg | translate"
                    />
                </template>
                <template v-else>
                    <p class="info-text inline">{{ 'By clicking on the continue button I agree with the' | translate }}
                        <template v-if="langIsGerman">
                            <router-link :to="{ path: $t('privacy') }" target='_blank'> {{ 'Privacy' | translate }}</router-link>, den
                            <router-link :to="{ path: $t('terms-and-conditions') }" target='_blank'> {{ 'terms' | translate }}</router-link> {{ 'and' | translate }}
                            <router-link :to="{ path: $t('cookie-policy') }" target='_blank'> {{ 'cookies' | translate }}</router-link>
                        </template>
                        <template v-else>
                            <router-link :to="{ path: $t('terms-and-conditions') }" target='_blank'> {{ 'terms' | translate }}</router-link>,
                            <router-link :to="{ path: $t('privacy') }" target='_blank'> {{ 'privacy' | translate }}</router-link> {{ 'and' | translate }}
                            <router-link :to="{ path: $t('cookie-policy') }" target='_blank'> {{ 'cookies' | translate }}</router-link>
                        </template>
                        {{ 'and can confirm that I am 18 years or older.' | translate }}
                    </p>
                </template>
            </div>
            <div class="row btn-wrapper" v-if="!open_register_with">
                <div v-if="step_no > 1 && step_no != 5" class="col-xs-6">
                    <button data-cy="register-previous" class="btn btn-grey" @click.prevent="back">
                        <span>{{ 'Back' | translate }}</span>
                    </button>
                </div>
                <div :class="[isMobileSubmit ? 'col-xs-12 large-btn' : 'col-xs-6']">
                    <button data-cy="register-next" :class="[{'btn-pulse': (step_no == 1)}, 'btn btn-orange']" :disabled="step_no == 4  && Boolean(nick_name.error_msg)" @click.prevent="next" v-if="step_no < 5">
                        <span>{{ 'CONTINUE' | translate }}</span>
                    </button>
                    <button data-cy="register-submit" class="btn btn-orange" :disabled="allowSubmit || btn_submit_disabled" @mousedown="next" v-else>
                        <span v-html="boxButton" />
                        <i class="fa fa-spinner fa-pulse" v-if="btn_submit_disabled"></i>
                    </button>
                </div>
            </div>
        </div>
    </form>
</template>

<script>
    import ProgressBar from '@theme-views/public/partials/PRegisterCard/ProgressBar'
    import SelectWrapper from '@theme-views/common/SelectWrapper'
    import PRegisterCard from '@views/public/partials/PRegisterCard/p_register_card'
    import RegisterFollowUp from '@views/user/partials/RegisterFollowUpPopUp/register_follow_up'
    import {checkNull, safeRedirect, strContains} from '@/helpers'
    import Toast from '@theme-views/common/Toast'
    import FormNotification from '@theme-views/common/FormNotification'

    export default {
        name: 'p-register-card-form',
        mixins: [PRegisterCard],
        components: {
            ProgressBar,
            Toast,
            SelectWrapper,
            ...RegisterFollowUp.components,
            FormNotification,
        },
        data() {
            return {
                step_no: 1,
                age: {
                    val: '',
                    error_msg: ''
                },
                general_message: null,
                is_success: true,
                nick_name: RegisterFollowUp.data().nick_name,
                nick_name_limit: 16,
                nick_name_diff_limit: 16,
                temp_nick_name: '',
                email: RegisterFollowUp.data().email,
                emails_fill: [],
                emails_suggest: [],
                email_ctr: '',
                email_ac: 'on',
                btn_submit_disabled: false,
                token: '',
                cdn_url: window.APP_CONFIG.CDN_URL,
                provider: null,
                provider_id: null,
                with_google_connect: window.APP_CONFIG.WITH_GOOGLE_CONNECT,
                with_yahoo_connect: window.APP_CONFIG.WITH_YAHOO_CONNECT,
                with_apple_connect: window.APP_CONFIG.WITH_APPLE_CONNECT || false,
                with_microsoft_connect: window.APP_CONFIG.WITH_MICROSOFT_CONNECT,
                error_goauth: false,
                display_terms: window.APP_CONFIG.PROFILE_CONFIRMATION,
                content: window.APP_CONFIG.LANDING_PAGE_CONTENT,
                check_terms: {
                    val: false,
                    error_msg: '',
                },
                open_register_with: null,
                is_new: true,
                sex_seeking_val: '',
            }
        },
        methods: {
            next() {
                if(this.step_no == 1) {
                    this.sex_seeking_val = this.preference
                    this.generateNickName()
                }

                if(this.step_no == 5) {
                    if(this.display_terms && !this.check_terms.val) {
                        this.check_terms.error_msg = 'Please accept the terms and conditions to continue'
                        return;
                    }

                    if(this.email.val === '') {
                        this.email.error_msg = 'Enter valid Email'
                        return;
                    }

                    if(this.email.error_msg !== '') {
                        return;
                    }

                    this.check_terms.error_msg = ''

                    this.register()
                }

                if(this.step_no < 5) this.step_no++
                this.$store.commit('pub/setStepNo', this.step_no)
            },
            back() {
                if(this.step_no > 1) this.step_no--
                this.$store.commit('pub/setStepNo', this.step_no)
            },
            redirectToBlockRegister(res) {
                let name = 'blocked-register';
                if (res.oauth) {
                    name = 'failed-oauth'
                }
                this.$router.push({ name, query:
                        {
                            redirect: res.redirect || null
                        }
                })
            },
            register() {
                this.btn_submit_disabled = true
                let params = {
                    sex_seeking: this.preference,
                    country_id: this.country,
                    nick_name: this.nick_name.val,
                    age: this.age.val,
                    province_id:this.province,
                    provider:this.provider,
                    provider_id:this.provider_id,
                    email: this.email.val,
                }
                this.$store.dispatch('auth/registerFinish', { params }).then(res => {
                    if(res.data && res.data.events) {
                        this.$store.dispatch('pub/allowLeave', true)
                        this.pushDataLayer(res)
                    }
                    if (res.data && res.data.trial && res.data.token) {
                        this.$store.dispatch('cookies/setORegister', null)
                        this.startTrial(res.data.token, res.data.trial, res.data.key)
                    } else {
                        this.$store.dispatch('cookies/setORegister', null)
                        this.logRegisterEvent(res)
                        this.btn_submit_disabled = false
                        if(this.provider && res.data.auto_login_url) {
                            safeRedirect(res.data.auto_login_url)
                            this.$store.commit('auth/setLoggedInTime', Date.now())
                        } else {
                            this.token = res.data.key
                            this.$store.dispatch('cookies/setOUser', { id: res.data.user_id })
                            // handle activate email registration
                            this.$router.push({ name: 'user-home', query: {
                              email: this.email.val,
                              age: this.age.val,
                              nick_name: this.nick_name.val,
                              province_id:this.province,
                              provider:this.provider,
                              provider_id:this.provider_id,
                            },
                            params: {
                              token_resend: this.token,
                              error: 'no_account',
                            }
                          })
                        }
                    }
                }, (res) => {
                    this.btn_submit_disabled = false
                    this.is_success = false

                    if(res.data.error_registration) {
                        this.redirectToBlockRegister(res.data.error_registration)
                    } else {
                        if (res.data.email && this.isOauthEmail) {
                            this.checkEmailProvider()
                            return; //don't show error
                        }
                        if (res.data.email) {
                            this.email.error_msg = res.data.email[0]
                            this.general_message = res.data.email[0]
                        }
                        else if (res.data.nick_name) {
                            this.step_no = 4
                            this.nick_name.error_msg = 'Nickname not available'
                            this.general_message = res.data.nick_name[0]
                        }
                        else {
                            this.step_no = 1
                            this.general_message = "Something went wrong."
                        }
                    }
                    console.log(res)
                })
            },
            resetForm() {
                console.log('reset form')
                this.$store.commit('pub/setStepNo', 1)
                this.age.val = 18
                this.step_no = 1
                this.token = ''
                this.email.val = ''
            },
            openPopUpError(props) {
                this.$root.$children[0].changeModalContent({
                    name: 'register-error-pop-up',
                    hasCloseButton: true,
                    alignCenter: true,
                    canCloseBackDrop: false,
                    extraClasses: "mobile-dark centered mobile-scrolled",
                    mobileBreakpoint: 1199,
                    props,
                    onClose: this.resetForm()
                })
                this.$root.$children[0].$emit('open-modal')
            },
            redirectOauth(provider) {
                let params = {};
                ['nick_name', 'email', 'age'].map(key => {
                    if(this[key].val) {
                        params[key] = this[key].val
                    }
                })
                params['seeking'] = this.preference
                params['country_id'] = this.country
                params['province_id'] = this.province

                let sd_config = this.$store.getters['cookies/getSdConfig'];
                params = {...params, ...sd_config}

                const queryString = Object.keys(params).map(function(key) {
                    return key + '=' + params[key]
                }).join('&');
                safeRedirect(`${window.location.origin}/oauth/redirect/${provider}` + (queryString.length ? '?' + queryString : ''))
            },
            validateInput: RegisterFollowUp.methods.validateInput,
            checkUniqueNickname: RegisterFollowUp.methods.checkUniqueNickname,
            selectedEmail: RegisterFollowUp.methods.selectedEmail,
            hoverSuggest: RegisterFollowUp.methods.hoverSuggest,
            generateNickName: RegisterFollowUp.methods.generateNickName,
            checkEmail: RegisterFollowUp.methods.checkEmail,
            checkEmailProvider: RegisterFollowUp.methods.checkEmailProvider,
            validateEmailAddress: RegisterFollowUp.methods.validateEmailAddress,
            hidelist: RegisterFollowUp.methods.hidelist,
            pushDataLayer: RegisterFollowUp.methods.pushDataLayer,
            logRegisterEvent: RegisterFollowUp.methods.logRegisterEvent,
            registerWith: RegisterFollowUp.methods.registerWith,
            cancelRegistrationWithOauth: RegisterFollowUp.methods.cancelRegistrationWithOauth,
            capitalize: RegisterFollowUp.methods.capitalize,
            startTrial: RegisterFollowUp.methods.startTrial,
        },
        computed: {
            ages: RegisterFollowUp.computed.ages,
            showDesktop: RegisterFollowUp.computed.showDesktop,
            emailVal: RegisterFollowUp.computed.emailVal,
            emailPlaceholder: RegisterFollowUp.computed.emailPlaceholder,
            oauthProviders: RegisterFollowUp.computed.oauthProviders,
            langIsGerman: RegisterFollowUp.computed.langIsGerman,
            boxSlogan() {
                return this.content.registration_box_slogan ? this.content.registration_box_slogan : this.$t('Find people near you')
            },
            isMobileSubmit() {
                return !this.showDesktop && this.step_no == 5
            },
            allowSubmit() {
                if(this.email.val && !this.email.error_msg && !this.email.is_loading)
                    return false

                return true
            },
            isOauthEmail() {
                const whitelistedProviders = [
                    /@yahoo\./,
                    /@Ymail\./,
                    /@ymail\./,
                    /@myyahoo\./,
                    /@rocketmail\./,
                    /@Y7mail\./,
                    /@y7mail\./
                ];
                if (this.with_yahoo_connect && strContains(this.email.val, whitelistedProviders)) {
                    return true
                }
                return;
            }
        },
        mounted() {
            let query = this.$route.query
            if(checkNull(query['provider']) && checkNull(query['provider_id'])) {
                this.email.val = query['email']
                this.provider = query['provider']
                this.provider_id = query['provider_id']
                this.preference = query['seeking']
            }

            if(checkNull(query['seeking']) && checkNull(query['nick_name']) && checkNull(query['age'])) {
                this.preference = query['seeking']
                this.country = query['country_id']
                this.province = query['province_id']
                this.age.val = query['age']
                this.nick_name.val = query['nick_name']
                this.step_no = 5
            } else {
                this.age.val = 18
                this.step_no = this.$store.getters['pub/stepNo']
            }

            //handle error
            if(checkNull(query['error']) && query['error'] === 'no_account' && checkNull(query['email'])) {
                this.email.val = query['email']
                this.token = query['token_resend']
                this.error_goauth = true

                this.$router.push({ name: 'user-home', query: {
                    email: query['email'],
                    age: this.age.val,
                    nick_name: this.nick_name.val,
                  },
                  params: {
                    token_resend: query['token_resend'],
                    error: this.error_goauth
                  }
                })
            }

            this.$store.dispatch('pub/getEmailAutoFillDomains', { country_id: this.country })
                .then(res => {
                    this.emails_fill = res.data.domains
                }, res => {
                    console.log(res)
                })
        },
        watch: {
            emailVal: RegisterFollowUp.watch.emailVal
        }
    }
</script>

<style lang="scss" src="./form.scss"></style>
